import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import './Portfolio.css';

const IMAGE_WIDTHS = [320, 480, 960, 1440, 1920];

const stripExt = (filename) => {
  const i = filename.lastIndexOf('.');
  return i === -1 ? filename : filename.slice(0, i);
};

const splitPath = (p) => {
  const i = p.lastIndexOf('/');
  if (i === -1) return { dir: '', file: p };
  return { dir: p.slice(0, i), file: p.slice(i + 1) };
};

const baseOptimizedUrl = (originalUrl) => {
  const { dir, file } = splitPath(originalUrl);
  const base = stripExt(file);
  return `/_img/portfolio${dir}/${base}`;
};

const buildSrcSet = (baseUrl, ext) =>
  IMAGE_WIDTHS.map((w) => `${baseUrl}-${w}.${ext} ${w}w`).join(', ');

const buildSrcSetUpTo = (baseUrl, ext, maxWidth) =>
  IMAGE_WIDTHS.filter((w) => w <= maxWidth).map((w) => `${baseUrl}-${w}.${ext} ${w}w`).join(', ');

const pickWidth = () => {
  if (typeof window === 'undefined') return 1440;
  const target = Math.min(1920, Math.ceil(window.innerWidth * (window.devicePixelRatio || 1)));
  for (const w of IMAGE_WIDTHS) {
    if (w >= target) return w;
  }
  return IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];
};

const ResponsivePicture = ({
  originalUrl,
  alt,
  className,
  sizes,
  fallbackWidth,
  loading,
  fetchPriority,
  progressive,
  intrinsicWidth,
  intrinsicHeight
}) => {
  const baseUrl = baseOptimizedUrl(originalUrl);

  const targetWidth = progressive ? pickWidth() : fallbackWidth;

  const ladder = useMemo(() => {
    if (!progressive) return IMAGE_WIDTHS;
    const previewWidth = IMAGE_WIDTHS[0];
    return targetWidth === previewWidth ? [previewWidth] : [previewWidth, targetWidth];
  }, [progressive, targetWidth]);

  const [stageIdx, setStageIdx] = useState(0);

  useLayoutEffect(() => {
    if (!progressive) return;
    setStageIdx(0);
  }, [progressive, originalUrl]);

  useEffect(() => {
    if (!progressive) return;
    if (stageIdx >= ladder.length - 1) return;
    let cancelled = false;
    const nextStageIdx = stageIdx + 1;
    const nextWidth = ladder[nextStageIdx];

    prefetchOptimized(originalUrl, nextWidth).then((res) => {
      if (cancelled || !res.ok) return;
      setStageIdx((i) => (i === stageIdx ? nextStageIdx : i));
    });

    return () => {
      cancelled = true;
    };
  }, [progressive, stageIdx, ladder, originalUrl]);

  const currentWidth = progressive ? ladder[Math.min(stageIdx, ladder.length - 1)] : fallbackWidth;

  const avifSet = progressive ? buildSrcSetUpTo(baseUrl, 'avif', currentWidth) : buildSrcSet(baseUrl, 'avif');
  const webpSet = progressive ? buildSrcSetUpTo(baseUrl, 'webp', currentWidth) : buildSrcSet(baseUrl, 'webp');
  const jpgSet = progressive ? buildSrcSetUpTo(baseUrl, 'jpg', currentWidth) : buildSrcSet(baseUrl, 'jpg');
  const src = `${baseUrl}-${currentWidth}.jpg`;

  return (
    <picture>
      <source type="image/avif" srcSet={avifSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSet} sizes={sizes} />
      <img
        src={src}
        srcSet={jpgSet}
        sizes={sizes}
        alt={alt}
        className={className}
        width={intrinsicWidth}
        height={intrinsicHeight}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
      />
    </picture>
  );
};

const prefetchImage = (url) =>
  new Promise((resolve) => {
    let settled = false;
    const img = new Image();
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    img.onload = async () => {
      if (typeof img.decode === 'function') {
        try {
          await img.decode();
        } catch {
          finish({ ok: false, url });
          return;
        }
      }
      finish({ ok: true, url, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => finish({ ok: false, url });
    img.src = url;
  });

const prefetchOptimized = async (originalUrl, width) => {
  const base = baseOptimizedUrl(originalUrl);
  const w = width || pickWidth();
  const candidates = [`${base}-${w}.avif`, `${base}-${w}.webp`, `${base}-${w}.jpg`];
  for (const url of candidates) {
    const res = await prefetchImage(url);
    if (res.ok) return res;
  }
  return { ok: false };
};

const getModalImageMaxFractions = (viewportWidth) => {
  if (!viewportWidth) return { maxWidth: 0.8, maxHeight: 0.6 };
  const w = viewportWidth;
  if (w <= 480) return { maxWidth: 0.9, maxHeight: 0.45 };
  if (w <= 768) return { maxWidth: 0.9, maxHeight: 0.5 };
  if (w <= 1024) return { maxWidth: 0.85, maxHeight: 0.55 };
  return { maxWidth: 0.8, maxHeight: 0.6 };
};

const computeModalImageBox = (aspectRatio, viewportWidth, viewportHeight) => {
  if (!aspectRatio || !viewportWidth || !viewportHeight) return null;
  const { maxWidth, maxHeight } = getModalImageMaxFractions(viewportWidth);
  const maxWidthPx = viewportWidth * maxWidth;
  const maxHeightPx = viewportHeight * maxHeight;
  const widthPx = Math.min(maxWidthPx, maxHeightPx * aspectRatio);
  const heightPx = widthPx / aspectRatio;
  return {
    width: Math.max(1, Math.round(widthPx)),
    height: Math.max(1, Math.round(heightPx))
  };
};

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [modalAspectRatio, setModalAspectRatio] = useState(null);
  const [viewportSize, setViewportSize] = useState(() => {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
  });
  const sectionRef = useRef(null);
  const switchTokenRef = useRef(0);
  const requestedIndexRef = useRef(0);
  const pendingIndexRef = useRef(null);
  const swipeRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startedOnButton: false
  });

  useEffect(() => {
    const onResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    const setVvh = () => {
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--vvh', `${h * 0.01}px`);
    };

    setVvh();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', setVvh);
      vv.addEventListener('scroll', setVvh);
    } else {
      window.addEventListener('resize', setVvh);
    }

    return () => {
      if (vv) {
        vv.removeEventListener('resize', setVvh);
        vv.removeEventListener('scroll', setVvh);
      } else {
        window.removeEventListener('resize', setVvh);
      }
      document.documentElement.style.removeProperty('--vvh');
    };
  }, [selectedProject]);

  useEffect(() => {
    requestedIndexRef.current = currentImageIndex;
  }, [currentImageIndex]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      id: 1,
      description: 'дизайн-проект 68 м²',
      coverImage: '/1%20проект%20(68м2)/1.JPG',
      images: [
        '/1%20проект%20(68м2)/1.JPG',
        '/1%20проект%20(68м2)/2.JPG',
        '/1%20проект%20(68м2)/3.JPG',
        '/1%20проект%20(68м2)/4.JPG',
        '/1%20проект%20(68м2)/5.JPG',
        '/1%20проект%20(68м2)/6.JPG',
        '/1%20проект%20(68м2)/7.JPG',
        '/1%20проект%20(68м2)/8.JPG',
        '/1%20проект%20(68м2)/9.JPG',
        '/1%20проект%20(68м2)/10.JPG',
        '/1%20проект%20(68м2)/11.JPG',
        '/1%20проект%20(68м2)/12.JPG'
      ]
    },
    {
      id: 2,
      description: 'дизайн-проект 86 м²',
      coverImage: '/2%20проект%20(86м2)/1.JPG',
      images: [
        '/2%20проект%20(86м2)/1.JPG',
        '/2%20проект%20(86м2)/2.JPG',
        '/2%20проект%20(86м2)/3.JPG',
        '/2%20проект%20(86м2)/4.JPG',
        '/2%20проект%20(86м2)/5.JPG',
        '/2%20проект%20(86м2)/6.JPG',
        '/2%20проект%20(86м2)/7.JPG',
        '/2%20проект%20(86м2)/8.JPG',
        '/2%20проект%20(86м2)/9.JPG',
        '/2%20проект%20(86м2)/10.JPG',
        '/2%20проект%20(86м2)/11.JPG',
        '/2%20проект%20(86м2)/12.JPG',
        '/2%20проект%20(86м2)/13.JPG',
        '/2%20проект%20(86м2)/14.JPG',
        '/2%20проект%20(86м2)/15.JPG',
        '/2%20проект%20(86м2)/16.JPG',
        '/2%20проект%20(86м2)/17.JPG',
        '/2%20проект%20(86м2)/18.JPG',
        '/2%20проект%20(86м2)/19.JPG',
        '/2%20проект%20(86м2)/20.JPG',
        '/2%20проект%20(86м2)/21.JPG'
      ]
    },
    {
      id: 3,
      description: 'дизайн-проект 61 м²',
      coverImage: '/3%20проект%20(61м2,реализованный)/1.JPG',
      images: [
        '/3%20проект%20(61м2,реализованный)/1.JPG',
        '/3%20проект%20(61м2,реализованный)/2.JPG',
        '/3%20проект%20(61м2,реализованный)/3.JPG',
        '/3%20проект%20(61м2,реализованный)/4.JPG',
        '/3%20проект%20(61м2,реализованный)/5.JPG',
        '/3%20проект%20(61м2,реализованный)/6.JPG',
        '/3%20проект%20(61м2,реализованный)/7.JPG',
        '/3%20проект%20(61м2,реализованный)/8.JPG',
        '/3%20проект%20(61м2,реализованный)/9.JPG',
        '/3%20проект%20(61м2,реализованный)/10.JPG',
        '/3%20проект%20(61м2,реализованный)/11.JPG',
        '/3%20проект%20(61м2,реализованный)/12.JPG',
        '/3%20проект%20(61м2,реализованный)/13.JPG',
        '/3%20проект%20(61м2,реализованный)/14.JPG',
        '/3%20проект%20(61м2,реализованный)/15.JPG',
        '/3%20проект%20(61м2,реализованный)/16.JPG'
      ]
    },
    {
      id: 4,
      description: 'дизайн-проект 98 м²',
      coverImage: '/4%20проект%20(98%20м2,реализованный)/1.JPG',
      images: [
        '/4%20проект%20(98%20м2,реализованный)/1.JPG',
        '/4%20проект%20(98%20м2,реализованный)/2.JPG',
        '/4%20проект%20(98%20м2,реализованный)/3.JPG',
        '/4%20проект%20(98%20м2,реализованный)/4.JPG',
        '/4%20проект%20(98%20м2,реализованный)/5.JPG',
        '/4%20проект%20(98%20м2,реализованный)/6.JPG',
        '/4%20проект%20(98%20м2,реализованный)/7.JPG',
        '/4%20проект%20(98%20м2,реализованный)/8.JPG',
        '/4%20проект%20(98%20м2,реализованный)/9.JPG',
        '/4%20проект%20(98%20м2,реализованный)/10.JPG',
        '/4%20проект%20(98%20м2,реализованный)/11.JPG',
        '/4%20проект%20(98%20м2,реализованный)/12.JPG',
        '/4%20проект%20(98%20м2,реализованный)/13.JPG',
        '/4%20проект%20(98%20м2,реализованный)/14.JPG',
        '/4%20проект%20(98%20м2,реализованный)/15.JPG',
        '/4%20проект%20(98%20м2,реализованный)/16.JPG'
      ]
    },
    {
      id: 5,
      description: 'дизайн-проект 213 м²',
      coverImage: '/5%20проект%20(213м2)/1.JPG',
      images: [
        '/5%20проект%20(213м2)/1.JPG',
        '/5%20проект%20(213м2)/2.JPG',
        '/5%20проект%20(213м2)/3.JPG',
        '/5%20проект%20(213м2)/4.JPG',
        '/5%20проект%20(213м2)/5.JPG',
        '/5%20проект%20(213м2)/6.JPG',
        '/5%20проект%20(213м2)/7.JPG',
        '/5%20проект%20(213м2)/8.JPG',
        '/5%20проект%20(213м2)/9.JPG',
        '/5%20проект%20(213м2)/10.JPG',
        '/5%20проект%20(213м2)/11.JPG',
        '/5%20проект%20(213м2)/12.JPG',
        '/5%20проект%20(213м2)/13.JPG',
        '/5%20проект%20(213м2)/14.JPG',
        '/5%20проект%20(213м2)/15.JPG',
        '/5%20проект%20(213м2)/16.JPG',
        '/5%20проект%20(213м2)/17.JPG'
      ]
    },
    {
      id: 6,
      description: 'дизайн-проект 41 м²',
      coverImage: '/6%20проект%20(41%20м2)/1.JPG',
      images: [
        '/6%20проект%20(41%20м2)/1.JPG',
        '/6%20проект%20(41%20м2)/2.JPG',
        '/6%20проект%20(41%20м2)/3.JPG',
        '/6%20проект%20(41%20м2)/4.JPG',
        '/6%20проект%20(41%20м2)/5.JPG',
        '/6%20проект%20(41%20м2)/6.JPG',
        '/6%20проект%20(41%20м2)/7.JPG',
        '/6%20проект%20(41%20м2)/8.JPG',
        '/6%20проект%20(41%20м2)/9.JPG',
        '/6%20проект%20(41%20м2)/10.JPG',
        '/6%20проект%20(41%20м2)/11.JPG'
      ]
    },
    {
      id: 7,
      description: 'дизайн-проект 43,8 м²',
      coverImage: '/7%20проект%20(43,8%20м2)/1.JPG',
      images: [
        '/7%20проект%20(43,8%20м2)/1.JPG',
        '/7%20проект%20(43,8%20м2)/2.JPG',
        '/7%20проект%20(43,8%20м2)/3.JPG',
        '/7%20проект%20(43,8%20м2)/4.JPG',
        '/7%20проект%20(43,8%20м2)/5.JPG',
        '/7%20проект%20(43,8%20м2)/6.JPG',
        '/7%20проект%20(43,8%20м2)/7.JPG',
        '/7%20проект%20(43,8%20м2)/8.JPG',
        '/7%20проект%20(43,8%20м2)/9.JPG',
        '/7%20проект%20(43,8%20м2)/10.JPG',
        '/7%20проект%20(43,8%20м2)/11.JPG',
        '/7%20проект%20(43,8%20м2)/12.JPG',
        '/7%20проект%20(43,8%20м2)/13.JPG',
        '/7%20проект%20(43,8%20м2)/14.JPG',
        '/7%20проект%20(43,8%20м2)/15.JPG',
        '/7%20проект%20(43,8%20м2)/16.JPG',
        '/7%20проект%20(43,8%20м2)/17.JPG'
      ]
    },
    {
      id: 8,
      description: 'дизайн-проект 77 м²',
      coverImage: '/8%20проект%20(77%20м2)/1.JPG',
      images: [
        '/8%20проект%20(77%20м2)/1.JPG',
        '/8%20проект%20(77%20м2)/2.JPG',
        '/8%20проект%20(77%20м2)/3.JPG',
        '/8%20проект%20(77%20м2)/4.JPG',
        '/8%20проект%20(77%20м2)/5.JPG',
        '/8%20проект%20(77%20м2)/6.JPG',
        '/8%20проект%20(77%20м2)/7.JPG',
        '/8%20проект%20(77%20м2)/8.JPG',
        '/8%20проект%20(77%20м2)/9.JPG',
        '/8%20проект%20(77%20м2)/10.JPG',
        '/8%20проект%20(77%20м2)/11.JPG',
        '/8%20проект%20(77%20м2)/12.JPG',
        '/8%20проект%20(77%20м2)/13.JPG',
        '/8%20проект%20(77%20м2)/14.JPG',
        '/8%20проект%20(77%20м2)/15.JPG',
        '/8%20проект%20(77%20м2)/16.JPG',
        '/8%20проект%20(77%20м2)/17.JPG',
        '/8%20проект%20(77%20м2)/18.JPG',
        '/8%20проект%20(77%20м2)/19.JPG',
        '/8%20проект%20(77%20м2)/20.JPG',
        '/8%20проект%20(77%20м2)/21.JPG',
        '/8%20проект%20(77%20м2)/22.JPG',
        '/8%20проект%20(77%20м2)/23.JPG',
        '/8%20проект%20(77%20м2)/24.JPG',
        '/8%20проект%20(77%20м2)/25.JPG',
        '/8%20проект%20(77%20м2)/26.JPG',
        '/8%20проект%20(77%20м2)/27.JPG',
        '/8%20проект%20(77%20м2)/28.JPG',
        '/8%20проект%20(77%20м2)/29.JPG',
        '/8%20проект%20(77%20м2)/30.JPG',
        '/8%20проект%20(77%20м2)/31.JPG',
        '/8%20проект%20(77%20м2)/32.JPG'
      ]
    },
    {
      id: 9,
      description: 'дизайн-проект 35 м²',
      coverImage: '/9%20проект%20(35м2)/1.JPG',
      images: [
        '/9%20проект%20(35м2)/1.JPG',
        '/9%20проект%20(35м2)/2.JPG',
        '/9%20проект%20(35м2)/3.JPG',
        '/9%20проект%20(35м2)/4.JPG',
        '/9%20проект%20(35м2)/5.JPG',
        '/9%20проект%20(35м2)/6.JPG',
        '/9%20проект%20(35м2)/7.JPG',
        '/9%20проект%20(35м2)/8.JPG',
        '/9%20проект%20(35м2)/9.JPG',
        '/9%20проект%20(35м2)/10.JPG',
        '/9%20проект%20(35м2)/11.JPG',
        '/9%20проект%20(35м2)/12.JPG'
      ]
    }
  ];

  const openModal = useCallback(async (project) => {
    pendingIndexRef.current = null;
    const token = (switchTokenRef.current += 1);
    const res = await prefetchOptimized(project.images[0], 320);
    if (switchTokenRef.current !== token) return;
    if (!res.ok) return;
    if (res.width && res.height) {
      setModalAspectRatio(res.width / res.height);
    } else {
      setModalAspectRatio(null);
    }

    setSelectedProject(project);
    setCurrentImageIndex(0);
    requestedIndexRef.current = 0;
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
    requestedIndexRef.current = 0;
    setModalAspectRatio(null);
    pendingIndexRef.current = null;
    switchTokenRef.current += 1;
    document.body.style.overflow = 'unset';
  }, []);

  const requestImageIndex = useCallback(
    async (index) => {
      if (!selectedProject) return;
      const max = selectedProject.images.length - 1;
      const nextIndex = Math.max(0, Math.min(index, max));
      if (nextIndex === currentImageIndex) return;
      if (pendingIndexRef.current !== null) return;

      pendingIndexRef.current = nextIndex;
      const token = (switchTokenRef.current += 1);
      const url = selectedProject.images[nextIndex];
      const res = await prefetchOptimized(url, 320);
      if (switchTokenRef.current !== token) return;
      pendingIndexRef.current = null;
      if (!res.ok) return;
      if (res.width && res.height) {
        setModalAspectRatio(res.width / res.height);
      } else {
        setModalAspectRatio(null);
      }
      setCurrentImageIndex(nextIndex);
    },
    [selectedProject, currentImageIndex]
  );

  const nextImage = useCallback(() => {
    if (!selectedProject) return;
    requestImageIndex(currentImageIndex + 1);
  }, [selectedProject, currentImageIndex, requestImageIndex]);

  const prevImage = useCallback(() => {
    if (!selectedProject) return;
    requestImageIndex(currentImageIndex - 1);
  }, [selectedProject, currentImageIndex, requestImageIndex]);

  const handleTouchStart = useCallback(
    (e) => {
      if (!selectedProject) return;
      if (!e.touches || e.touches.length !== 1) {
        swipeRef.current.active = false;
        return;
      }

      const target = e.target;
      const startedOnButton = Boolean(target && target.closest && target.closest('button'));
      const t = e.touches[0];
      swipeRef.current.active = !startedOnButton;
      swipeRef.current.startedOnButton = startedOnButton;
      swipeRef.current.startX = t.clientX;
      swipeRef.current.startY = t.clientY;
      swipeRef.current.lastX = t.clientX;
      swipeRef.current.lastY = t.clientY;
    },
    [selectedProject]
  );

  const handleTouchMove = useCallback((e) => {
    if (!swipeRef.current.active) return;
    if (!e.touches || e.touches.length !== 1) return;
    const t = e.touches[0];
    swipeRef.current.lastX = t.clientX;
    swipeRef.current.lastY = t.clientY;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!swipeRef.current.active) {
      swipeRef.current.active = false;
      return;
    }

    const dx = swipeRef.current.lastX - swipeRef.current.startX;
    const dy = swipeRef.current.lastY - swipeRef.current.startY;
    swipeRef.current.active = false;

    if (Math.abs(dx) < 45) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.2) return;

    if (dx < 0) nextImage();
    else prevImage();
  }, [nextImage, prevImage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) return;

      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, nextImage, prevImage, closeModal]);

  useEffect(() => {
    if (!selectedProject) return;
    const nextIdx = currentImageIndex + 1;
    const prevIdx = currentImageIndex - 1;
    if (nextIdx < selectedProject.images.length) {
      prefetchOptimized(selectedProject.images[nextIdx], 320);
    }
    if (prevIdx >= 0) {
      prefetchOptimized(selectedProject.images[prevIdx], 320);
    }
  }, [selectedProject, currentImageIndex]);

  const isNarrowViewport = Boolean(viewportSize.width && viewportSize.width <= 768);
  const modalImageBox = isNarrowViewport ? null : computeModalImageBox(modalAspectRatio, viewportSize.width, viewportSize.height);

  return (
    <section id="portfolio" className="portfolio" ref={sectionRef}>
      <div className="portfolio-container">
        <h2 className={`portfolio-title ${isVisible ? 'animate' : ''}`}>ПОРТФОЛИО</h2>

        <div className="portfolio-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`portfolio-item ${isVisible ? 'animate' : ''}`}
              onClick={() => openModal(project)}
            >
              <div className="portfolio-image-container">
                <ResponsivePicture
                  originalUrl={project.coverImage}
                  alt={project.description}
                  className="portfolio-image"
                  sizes="(max-width: 768px) 92vw, (max-width: 1200px) 45vw, 400px"
                  fallbackWidth={960}
                  loading="lazy"
                  fetchPriority="low"
                  progressive={false}
                />
                <div className="portfolio-photo-count">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {project.images.length} фото
                </div>
                <div className="portfolio-overlay">
                  <div className="portfolio-overlay-content">
                    <p className="portfolio-project-description">{project.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedProject && (
          <div className="portfolio-modal" onClick={closeModal}>
            <div className="portfolio-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="portfolio-modal-close" onClick={closeModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              <div
                className="portfolio-modal-image-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
              >
                {currentImageIndex > 0 && (
                  <button className="portfolio-modal-arrow portfolio-modal-prev" onClick={prevImage}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}

                <ResponsivePicture
                  originalUrl={selectedProject.images[currentImageIndex]}
                  alt={`${selectedProject.description} - ${currentImageIndex + 1}`}
                  className="portfolio-modal-image"
                  sizes="(max-width: 768px) 95vw, 80vw"
                  fallbackWidth={320}
                  loading="eager"
                  fetchPriority="high"
                  progressive={true}
                  intrinsicWidth={modalImageBox ? modalImageBox.width : undefined}
                  intrinsicHeight={modalImageBox ? modalImageBox.height : undefined}
                />

                {currentImageIndex < selectedProject.images.length - 1 && (
                  <button className="portfolio-modal-arrow portfolio-modal-next" onClick={nextImage}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>

              <div className="portfolio-modal-info">
                <p className="portfolio-modal-description">{selectedProject.description}</p>
                <div className="portfolio-modal-counter">
                  {currentImageIndex + 1} / {selectedProject.images.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;
